import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import {
  AssetService,
  ModalRef,
  ModalService,
  ReactiveComponent,
  ToolingInfo,
  TwoButtonModalV2Component,
  TwoButtonModalV2,
  ModalModule,
  ChipV2Module,
  ButtonV2Module,
  AssetsModule,
  FileUploadV2Module,
} from '@schenck/rotec-components';
import { BehaviorSubject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { ToolingTaskInfo } from 'src/api/tooling-task.api';
import { UpsertToolingTaskWizardComponent } from 'src/pages/tooling-tasks/upsert-tooling-task-wizard/upsert-tooling-task-wizard.component';
import { ToolingTaskService } from 'src/services';
import { TranslatePipe, TranslateDirective } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { TaskListComponent } from '../task-list/task-list.component';

@Component({
  selector: 'ma-tooling-details-modal',
  templateUrl: './tooling-details-modal.component.html',
  styleUrls: ['./tooling-details-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ModalModule,
    TranslatePipe,
    TranslateDirective,
    ChipV2Module,
    TaskListComponent,
    ButtonV2Module,
    AsyncPipe,
    AssetsModule,
    FileUploadV2Module,
  ],
})
export class ToolingDetailsModalComponent extends ReactiveComponent {
  private readonly assetService = inject(AssetService);
  private readonly modalService = inject(ModalService);
  private readonly taskService = inject(ToolingTaskService);
  private readonly cdr = inject(ChangeDetectorRef);
  public modalRef = inject<ModalRef<ToolingInfo>>(ModalRef);

  private tooling$ = this.assetService.tooling$(this.modalRef.data.id);
  private machines$ = this.assetService.machinesThatHaveTooling$(this.tooling$);
  private loadTaskList$ = new BehaviorSubject<null>(null);

  public state = this.connect(
    {
      tooling: this.tooling$,
      machines: this.machines$,
      tasks: this.loadTaskList$.pipe(
        switchMap(() => this.taskService.tasksByTooling$(this.modalRef.data.id))
      ),
    },
    this.cdr
  );

  constructor() {
    super();
  }

  public openTask(id: string): void {
    this.taskService
      .get$(id)
      .pipe(
        switchMap(
          (machine: ToolingTaskInfo) =>
            this.modalService.open(UpsertToolingTaskWizardComponent, machine).events$
        ),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => this.loadTaskList$.next(null));
  }

  public deleteTask(id: string): void {
    this.taskService
      .get$(id)
      .pipe(
        switchMap((task: ToolingTaskInfo) =>
          this.modalService
            .open(TwoButtonModalV2Component, <TwoButtonModalV2>{
              title: '',
              text: task.scheduling
                ? `Do you really want to delete this task series: VALUE ?`
                : `Do you really want to delete this task: VALUE ?`,
              textParams: { value: task.title },
              cancelText: 'Cancel',
              confirmText: 'Delete',
              summary: 'Delete Tasks',
              darkMode: true,
              warning: true,
            })
            .events$.pipe(filter((shouldDelete: boolean) => !!shouldDelete))
        ),
        switchMap(() => this.taskService.delete$(id)),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => this.loadTaskList$.next(null));
  }
}
